// src/pages/auth/DeviceAuthPage.tsx
// Device authorization code display.

import { useState } from 'react';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, ErrorAlert } from '@react/components';
import { AuthLayout } from './AuthLayout';

export const DeviceAuthPage = observer(() => {
  const authnStore = useAuthn();
  const { t } = useI18n();

  const [userCode, setUserCode] = useState('');
  const [verificationUri, setVerificationUri] = useState('');
  const [, setDeviceCode] = useState('');
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const handleInitiate = async () => {
    setError(null);
    try {
      const resp = await authnStore.AuthorizeDevice({
        entity: {} as never,
        client_id: '',
        scope: '',
      });
      setUserCode(resp.user_code);
      setVerificationUri(resp.verification_uri);
      setDeviceCode(resp.device_code);
      startPolling(resp.device_code, resp.interval || 5);
    } catch (err) {
      setError(err);
    }
  };

  const startPolling = (code: string, interval: number) => {
    setPolling(true);
    const timer = setInterval(async () => {
      try {
        await authnStore.ExchangeDeviceToken({
          entity: {} as never,
          grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
          device_code: code,
          client_id: '',
        });
        clearInterval(timer);
        setPolling(false);
        window.location.href = '/dashboard';
      } catch {
        // keep polling
      }
    }, interval * 1000);
  };

  const formatCode = (code: string) => {
    if (code.length <= 4) return code;
    const mid = Math.ceil(code.length / 2);
    return `${code.slice(0, mid)}-${code.slice(mid)}`;
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle as="h1" className="text-2xl">{t('auth.deviceAuthTitle')}</CardTitle>
          <CardDescription>{t('auth.deviceAuthSubtitle')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6 text-center">
          {error ? <ErrorAlert error={error} /> : null}

          {!userCode && !polling && (
            <Button onClick={handleInitiate} isLoading={authnStore.isLoading}>
              {t('auth.generateDeviceCode')}
            </Button>
          )}

          {userCode && (
            <>
              <div className="p-6 bg-muted rounded-xl">
                <p className="text-xs text-muted-foreground mb-2">{t('auth.verificationCode')}</p>
                <p className="text-3xl font-mono font-bold tracking-widest text-foreground">
                  {formatCode(userCode)}
                </p>
              </div>

              {verificationUri && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('auth.goTo')}</p>
                  <a href={verificationUri} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:text-primary/80 font-medium">
                    {verificationUri}
                  </a>
                </div>
              )}

              {polling && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  {t('auth.waitingForAuth')}
                </div>
              )}
            </>
          )}
        </CardContent>

        <CardFooter />
      </Card>
    </AuthLayout>
  );
});

export default DeviceAuthPage;
